import { computed, makeObservable, observable, runInAction, action } from 'mobx'
import { ProPayoutsPorts } from './ports'
import memoize from 'memoizee'
import { task } from 'mobx-task'
import {
  ProPayoutStore,
  ListHistoricalTransactionsQuery,
  ProPayoutSummary,
} from './ProPayoutStore'
import {
  DateRange,
  DateRangeFilter,
} from 'infra/FilterControls/DateRange/DateRangeFilter'
import { defer, combineLatest, BehaviorSubject, firstValueFrom } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { shareLatest, whileActive, every } from 'utils/rx-util'
import { ProHistoricalTransaction } from './ProHistoricalTransaction'
import { ProHistoricalTransactionViewModel } from './ProHistoricalTransactionViewModel'
import { ViewModelList } from 'infra/ViewModelList'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { FiltersViewModel, FilterItemSource } from 'infra/FiltersViewModel'
import { SearchTextViewModel } from 'infra/SearchTextViewModel'
import { AlertState } from 'alerts/Alert'
import { toStream } from 'utils/mobx-util'
import { TransactionSourceType } from '../TransactionSourceType'
import { SelectFilter } from '../../infra/FilterControls/Select/SelectFilter'

/**
 * Filter for historical transactions.
 */
export type AdminTransactionHistoryFilter = {
  completedRange: DateRange
  transactionSource: TransactionSourceType[]
}

/**
 * View model for the Pro Payouts view.
 */
export class ProPayoutsViewModel {
  /**
   * Tracks whether the view model is active.
   */
  private readonly _active = new BehaviorSubject<boolean>(false)

  /**
   * Transaction view model list.
   */
  private readonly transactionVms

  /**
   * The query to use.
   */
  private readonly query$ = defer(() =>
    combineLatest([
      this.searchText.debouncedValue$,
      this.filters.appliedFilters$,
    ]),
  ).pipe(
    map(
      ([searchText, filters]): ListHistoricalTransactionsQuery => ({
        searchText,
        dateCompleted: filters.completedRange,
        transactionSource: filters.transactionSource,
      }),
    ),
    shareLatest(),
  )

  /**
   * Fetch pipeline.
   */
  private readonly fetch$ = this.query$.pipe(
    // Only issue queries while...
    whileActive(
      every([
        //  ... the VM is active and
        this._active,
        // ... we have permission to view Pro transaction history
        toStream(() => this.canView, true),
      ]),
    ),

    switchMap((query) =>
      // Ignore errors here as we don't want to cancel the pipeline.
      this.transactionsScroller.fetch(query).catch(() => null),
    ),
  )

  /**
   * The search text.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Filters.
   */
  readonly filters = new FiltersViewModel<AdminTransactionHistoryFilter>({
    definitions: {
      completedRange: {
        icon: 'date-completed',
        title: 'Date',
        factory: (key, title) => new DateRangeFilter(key, title),
      },
      transactionSource: {
        icon: 'transaction-source',
        title: 'Source',
        factory: (key, title) =>
          new SelectFilter(
            key,
            title,
            FilterItemSource.static([
              {
                title: 'User',
                value: TransactionSourceType.User,
              },
              {
                title: 'Job',
                value: TransactionSourceType.Job,
              },
            ]),
            true,
          ),
      },
    },
  })

  /**
   * Scroller for transactions.
   */
  readonly transactionsScroller = new InfiniteScrollProvider<
    ProHistoricalTransaction,
    ListHistoricalTransactionsQuery
  >({
    limit: 200,
    startFetchTaskAsPending: true,
    fetchItems: (paging, ct, query = {}) =>
      this.proPayoutStore.listHistoricalTransactions(query, paging, ct),
  })

  /**
   * Whether the user is considered to be a Pro.
   *
   * @private
   */
  currentUserIsPro = false

  /**
   * The pro payout summary.
   */
  summary = ProPayoutSummary.default()

  /**
   * Constructor.
   */
  constructor(
    private readonly proPayoutStore: ProPayoutStore,
    private readonly adapter: ProPayoutsPorts,
  ) {
    this.checkCurrentUserProStatus = memoize(
      this.checkCurrentUserProStatus.bind(this),
      { promise: true, length: 0 },
    )
    makeObservable(this, {
      canView: computed,
      currentUserIsPro: observable,
      transactionsCount: computed,
      transactions: computed,
      filtersApplied: computed,
      clearFilters: action.bound,
      summary: observable.shallow,
    })

    this.transactionVms = new ViewModelList<
      ProHistoricalTransaction,
      ProHistoricalTransactionViewModel
    >({
      create: (model) => new ProHistoricalTransactionViewModel(model),
      models: () => this.transactionsScroller.items.slice(),
      activate: (vm) => vm.activate(),
      deactivate: (vm) => vm.deactivate(),
    })

    this.fetch$.subscribe()
  }

  /**
   * Gets the total count of transactions that match the server-based filter.
   */
  get transactionsCount() {
    return this.transactionsScroller.total
  }

  /**
   * The transactions that have been loaded.
   */
  get transactions() {
    return this.transactionVms.items
  }

  /**
   * Whether any filters have been applied, including search text.
   */
  get filtersApplied() {
    return !!this.searchText.value || this.filters.appliedFilterCount > 0
  }

  /**
   * Whether the current user can view Pro Payouts.
   */
  get canView() {
    return this.currentUserIsPro
  }

  /**
   * Activates the view model.
   */
  activate() {
    this._active.next(true)
    this.filters.activate()
    this.transactionVms.activate()
    this.prepare().then()
  }

  /**
   * Deactivates the view model.
   */
  deactivate() {
    this.filters.deactivate()
    this.transactionVms.deactivate()
    this._active.next(false)
  }

  /**
   * Clears any search filters.
   */
  clearFilters() {
    this.searchText.clear()
    this.filters.clearExplicitFilters()
  }

  /**
   * Checks whether the current user is a Pro.
   */
  async checkCurrentUserProStatus() {
    const currentUserIsPro = await this.adapter.currentUserIsPro()
    runInAction(() => {
      this.currentUserIsPro = currentUserIsPro
    })
  }

  /**
   * Pages through all transactions and exports all of them as CSV.
   */
  exportCsv = task.resolved(async () => {
    const handle = await this.adapter.downloader.requestDownload({
      suggestedName: 'transaction-history.csv',
      fileTypeDescription: 'CSV file',
      accept: { 'text/csv': ['.csv'] },
    })
    if (!handle) {
      return
    }

    const alert = this.adapter
      .newAlert()
      .withState(AlertState.Pending)
      .saying('Exporting', 'This may take a moment.')
      .show()
    try {
      // Get the query that is used for the current list.
      const query = await firstValueFrom(this.query$)

      // Download the historical transactions, pipe them to the file.
      await this.proPayoutStore
        .downloadHistoricalTransactionsAsCsv({
          workspaceIds: query.workspaceIds,
          dateCompleted: query.dateCompleted,
          searchText: query.searchText,
          transactionSource: query.transactionSource,
        })
        .then((csv) => handle.download(csv))

      alert
        .withState(AlertState.Success)
        .saying(
          'Export completed!',
          `The transaction history was exported to ${handle.fileName}.`,
        )
        .autoDismiss()
    } catch (err) {
      alert.fromError(err).autoDismiss()
    }
  })

  /**
   * Prepares the view model by retrieving information necessary to render
   * the overall screen.
   */
  prepare = task(async () => {
    await this.checkCurrentUserProStatus()
    if (this.currentUserIsPro) {
      // We don't need to wait for this.
      this.fetchSummary().then()
    }
  })

  /**
   * Fetches the pro payout summary.
   */
  fetchSummary = task(async () => {
    const summary = await this.proPayoutStore.fetchPayoutSummary()
    runInAction(() => {
      this.summary = summary
    })
  })
}
